import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Paper
} from '@mui/material';
import { APIHOST } from '../../common';

interface PasswordResetProps {
  onCancel: () => void;
  onSwitchToLogin: () => void;
}

export default function PasswordReset({ onCancel, onSwitchToLogin }: PasswordResetProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${APIHOST}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error?.message || 'Failed to send password reset email');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = email.trim().length > 0;

  if (success) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto' }}>
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
          <Typography variant="h6" gutterBottom color="success.main">
            Password Reset Email Sent
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If an account with that email exists, we've sent you a password reset link. 
            Please check your email and follow the instructions to reset your password.
          </Typography>
          <Button
            onClick={onSwitchToLogin}
            variant="contained"
            fullWidth
          >
            Back to Sign In
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom>
          Reset Your Password
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your email address and we'll send you a link to reset your password.
        </Typography>

        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={handleInputChange}
          disabled={loading}
          sx={{ mb: 2 }}
          autoComplete="email"
          autoFocus
        />

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={!isFormValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ mt: 2 }}
        >
          {loading ? 'Sending...' : 'Send Reset Email'}
        </Button>
      </Box>

      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Remember your password?{' '}
          <Button 
            onClick={onSwitchToLogin}
            sx={{ textTransform: 'none', p: 0 }}
            size="small"
          >
            Back to Sign In
          </Button>
        </Typography>
      </Box>
    </Box>
  );
}